/**
 * Variant B (Care+ weight_loss_2) only — second leg of a Google sign-up for an email
 * DoktorABC has never seen.
 *
 * The API redirects such a user back to `cameFromPath` with the temp profile in the
 * query string (`showOauthAdditionalInfoModal=true&email=…&externalAccessToken=…`).
 * Everywhere else in the funnel, SocialLoginButtons picks that up and shows the
 * "we need a few more details" popup (birthday / gender / name) itself. In Variant B
 * that component lives inside SignInModal, and a portal nested in a portal that
 * mounts in the same commit is appended to `modal-root` *before* its parent, so the
 * sign-in dimmer painted over the popup and the user only ever saw the login again.
 *
 * This is the same popup + completion request as in SocialLoginButtons (kept in sync
 * by hand — the shared component is deliberately untouched), rendered by
 * QuestionnaireVariantB as a sibling of SignInModal, never as its child.
 */

import React, { useState } from "react";
import PortaledModal from "../../../components/Modals/PortaledModal";
import OAuthAdditionalInfoModal from "../../../components/Modals/OAuthAdditionalInfoModal";
import * as http from "../../../utils/http";
import { useCustomer } from "../../../customHooks/useCustomer";
import * as SocialMediaTracker from "../../../PixelTrackers/SocialMediaTracker";
import { REGISTRATION_COMPLETED_EVENT } from "../../../utils/consts";

/** True when these params are the return leg of a Google sign-up that still needs details. */
export const isOAuthSignupCompletionUrl = (params: URLSearchParams): boolean =>
  params.get("showOauthAdditionalInfoModal") === "true";

interface OAuthSignupCompletionProps {
  isOpen: boolean;
  onClose: () => void;
  language: string;
  country: string;
  /**
   * Snapshot of the OAuth callback params taken when the questionnaire mounted. The host
   * cleans the URL right after and mounts this popup late (after loading / on the intro),
   * so reading `window.location.search` here would come up empty.
   */
  params: URLSearchParams;
  onCompleteSignup?: () => void;
  onFailureSignup?: () => void;
}

const OAuthSignupCompletion: React.FC<OAuthSignupCompletionProps> = ({
  isOpen,
  onClose,
  language,
  country,
  params,
  onCompleteSignup,
  onFailureSignup,
}) => {
  const { saveCustomer } = useCustomer();
  const [oauthParams] = useState(params);

  const handleSubmit = (formData: { dateOfBirth: string; gender: string; firstName: string; lastName: string }) => {
    const requestData = {
      birthday: formData.dateOfBirth,
      gender: formData.gender === "male" ? 1 : 0,
      firstName: formData.firstName || oauthParams.get("firstName") || "",
      lastName: formData.lastName || oauthParams.get("lastName") || "",
      provider: oauthParams.get("provider") || "",
      email: oauthParams.get("email") || "",
      externalAccessToken: oauthParams.get("externalAccessToken") || "",
      refreshToken: oauthParams.get("refreshToken") || "",
      language,
      country,
      trackingData: window.eventsTracking && window.eventsTracking.getData(),
    };

    http
      .postToServer("auth/complete-oauth-signup", requestData)
      .then((res) => {
        saveCustomer({
          customerID: res.data.customerID,
          uuid: res.data.uuid,
          token: res.data.token,
          email: res.data.email,
          optForSendables: res.data.optForSendables,
          fullName: res.data.customerFullName,
          loginSource: "questionnaire",
          provider: res.data.provider || "local",
          isSignup: true,
          gender: res.data?.gender,
          lastPaidCategoryMention: "",
        });

        SocialMediaTracker.track("pv", REGISTRATION_COMPLETED_EVENT, { email: res.data.email });

        onCompleteSignup && onCompleteSignup();
      })
      .catch((error) => {
        window.clientLogger.error("Error completing OAuth signup:", error);
        onFailureSignup && onFailureSignup();
      });
  };

  return (
    <PortaledModal shouldShowModal={isOpen} hideModal={() => {}}>
      <OAuthAdditionalInfoModal onSubmit={handleSubmit} onClose={onClose} language={language} />
    </PortaledModal>
  );
};

export default OAuthSignupCompletion;
